import { useState, useRef, useEffect, type ReactNode, useCallback } from "react";
import { createPortal } from "react-dom";
import { cn } from "@repo/utils";
import { DropdownButton } from "./dropdown-button.js";
import { DropdownMenuItemList } from "./dropdown-menu-item-list.js";
import type { ItemProps, EvaluatedItemProps } from "./dropdown-menu-item-list.js";
import type { IconProps } from "./dropdown.types.js";

type Props = {
  items?: EvaluatedItemProps[];
  trigger?: ReactNode;
  icon?: IconProps;
  className?: string;
  buttonClassName?: string;
  iconClassName?: string | ((isOpen: boolean) => string);
  menuClassName?: string;
  align?: "left" | "right" | "center";
  placement?: "bottom" | "top" | "left" | "right";
  children?: ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

type MenuPositioning = "absolute" | "fixed";

export function DropdownMenu({
  items,
  trigger,
  icon = "dots",
  className,
  buttonClassName,
  iconClassName,
  menuClassName,
  align = "right",
  placement = "bottom",
  children,
  isOpen: externalIsOpen = false,
  onOpenChange,
}: Props) {
  const menuPositioning: MenuPositioning = "absolute";
  const [isOpen, setIsOpenInternal] = useState(externalIsOpen);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const setIsOpen = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      setIsOpenInternal((prev) => {
        const next = typeof value === "function" ? value(prev) : value;
        if (next !== prev) {
          onOpenChange?.(next);
        }
        return next;
      });
    },
    [onOpenChange]
  );

  useEffect(() => {
    setIsOpenInternal(externalIsOpen);
  }, [externalIsOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        if (triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, setIsOpen]);

  const handleMenuItemClick = useCallback((item: ItemProps) => {
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  }, [setIsOpen]);

  const toggleOpen = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, [setIsOpen]);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !menuRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const menuWidth = menuRef.current.offsetWidth || 192;
    const menuHeight = menuRef.current.offsetHeight || 0;

    let top = 0;
    let left = 0;

    switch (placement) {
      case "top":
        top = rect.top + (menuPositioning === "absolute" ? window.scrollY : 0) - menuHeight - 8;
        break;
      case "bottom":
        top = rect.bottom + (menuPositioning === "absolute" ? window.scrollY : 0) + 8;
        break;
      case "left":
      case "right":
        top = rect.top + (menuPositioning === "absolute" ? window.scrollY : 0);
        break;
    }

    if (placement === "left") {
      left = rect.left + (menuPositioning === "absolute" ? window.scrollX : 0) - menuWidth - 8;
    } else if (placement === "right") {
      left = rect.right + (menuPositioning === "absolute" ? window.scrollX : 0) + 8;
    } else {
      switch (align) {
        case "left":
          left = rect.left + (menuPositioning === "absolute" ? window.scrollX : 0);
          break;
        case "right":
          left = rect.right + (menuPositioning === "absolute" ? window.scrollX : 0) - menuWidth;
          break;
        case "center":
          left =
            rect.left +
            (menuPositioning === "absolute" ? window.scrollX : 0) +
            rect.width / 2 -
            menuWidth / 2;
          break;
      }
    }

    //Boundary within viewport
    if ((menuPositioning as MenuPositioning) === "fixed") {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      if (left + menuWidth > viewportWidth) {
        left = viewportWidth - menuWidth - 8;
      }

      if (left < 8) {
        left = 8;
      }

      if (top + menuHeight > viewportHeight) {
        top = viewportHeight - menuHeight - 8;
      }

      if (top < 8) {
        top = 8;
      }
    }

    setMenuPosition({ top, left });
  }, [placement, align]);

  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      calculatePosition();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen, calculatePosition]);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        calculatePosition();
      });
    }
  }, [isOpen, calculatePosition]);

  const dropdownContent = isOpen && (
    <div
      ref={menuRef}
      className={cn(
        "animate-fade-in min-w-48 rounded-lg border border-gray-200 bg-white shadow-lg",
        menuClassName
      )}
      style={{
        position: menuPositioning,
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
        zIndex: 9999,
      }}
    >
      {children ? (
        children
      ) : items ? (
        <div className="py-1">
          <DropdownMenuItemList items={items} onClick={handleMenuItemClick} />
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      <div className={cn("relative inline-block", className)}>
        <DropdownButton
          className={buttonClassName}
          iconClassName={
            typeof iconClassName === "function" ? iconClassName(isOpen) : iconClassName
          }
          ref={triggerRef}
          trigger={trigger}
          toggleOpen={toggleOpen}
          isDDOpen={isOpen}
          icon={icon}
        />
      </div>
      {typeof document !== "undefined" &&
        dropdownContent &&
        createPortal(dropdownContent, document.body)}
    </>
  );
}
